import React, { useState, useEffect, useRef } from "react";
import { Anchor, ChevronDown, Check } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";

const ShotReferenceSelector = ({
  currentIndex,
  rows,
  selectedIndex,
  onChange,
  align = "left",
  previewPlacement = "right"
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredShot, setHoveredShot] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const containerRef = useRef(null);
  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);

  const availableShots = rows
    .map((row, idx) => ({ ...row, originalIndex: idx }))
    .filter((row, idx) => idx < currentIndex && row.generated_image);

  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSelect = (idx) => {
    onChange(idx);
    setIsOpen(false);
  };

  const selectedShot =
    selectedIndex !== null && selectedIndex !== undefined && rows[selectedIndex]
      ? rows[selectedIndex]
      : null;

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="h-6 px-2 rounded-md border flex items-center gap-1.5 cursor-pointer transition-all text-2xs"
        style={{
          backgroundColor: selectedShot ? `${theme.colors.accentPrimary}20` : 'var(--color-bg-tertiary)',
          borderColor: selectedShot ? `${theme.colors.accentPrimary}50` : 'var(--color-border-primary)',
          color: selectedShot ? theme.colors.accentSecondary : 'var(--color-text-muted)'
        }}
      >
        <Anchor size={10} />
        <span className="font-bold truncate max-w-[80px]">
          {selectedShot ? `Shot ${selectedShot.shot_number}` : "参考源..."}
        </span>
        <ChevronDown size={10} className="ml-auto opacity-50" />
      </div>

      {/* 下拉菜单 */}
      {mounted && (
        <div
            className={`absolute top-full mt-1 w-64 rounded-lg shadow-2xl z-[60] max-h-60 overflow-y-auto custom-scrollbar p-1 ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-100"} ${
                align === "right" ? "right-0" : "left-0"
            }`}
            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}
        >
          <div className="px-2 py-1 text-2xs font-bold uppercase border-b mb-1" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border-primary)' }}>
            选择历史镜头
          </div>
          {availableShots.length === 0 ? (
            <div className="p-2 text-center text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              前方暂无已生成镜头
            </div>
          ) : (
            availableShots.map((shot) => (
              <div
                key={shot.shot_number}
                onClick={() => handleSelect(shot.originalIndex)}
                onMouseEnter={() => { setHoveredShot(shot); setHoverRect(containerRef.current?.getBoundingClientRect() ?? null); }}
                onMouseLeave={() => setHoveredShot(null)}
                className="flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors relative"
                style={{
                  backgroundColor: selectedIndex === shot.originalIndex ? `${theme.colors.accentPrimary}20` : 'transparent',
                  color: selectedIndex === shot.originalIndex ? theme.colors.accentSecondary : 'var(--color-text-secondary)'
                }}
              >
                <div className="w-8 h-5 rounded overflow-hidden flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }}>
                  <img
                    src={shot.generated_image}
                    className="w-full h-full object-cover"
                    alt=""
                  />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-bold text-2xs leading-none mb-0.5" style={{ color: 'var(--color-text-primary)' }}>
                    Shot {shot.shot_number}
                  </span>
                  <span className="truncate text-2xs leading-none" style={{ color: 'var(--color-text-muted)' }}>
                    {shot.action || "无描述"}
                  </span>
                </div>
                {selectedIndex === shot.originalIndex && (
                  <Check size={12} className="ml-auto" style={{ color: theme.colors.accentPrimary }} />
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* 悬浮预览图 */}
      {isOpen && hoveredShot && (
        <div
          className="fixed z-[1000] w-80 rounded-xl overflow-hidden shadow-2xl pointer-events-none p-1 animate-in fade-in"
          style={{
            top: Math.min(
              hoverRect?.top || 0,
              window.innerHeight - 240
            ),
            left: (() => {
              const rect = hoverRect;
              if (!rect) return 0;
              const previewW = 320;
              if (previewPlacement === "left") {
                const leftPos = rect.left - previewW - 10;
                return leftPos > 10 ? leftPos : rect.right + 10;
              }
              const rightPos = rect.right + 10;
              return rightPos + previewW < window.innerWidth - 10 ? rightPos : rect.left - previewW - 10;
            })(),
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)'
          }}
        >
          <div className="aspect-video w-full rounded overflow-hidden relative" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
            <img
              src={hoveredShot.generated_image}
              className="w-full h-full object-cover"
              alt=""
            />
            <div className="absolute bottom-0 left-0 w-full p-1.5 text-2xs font-bold" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>
              Shot {hoveredShot.shot_number}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShotReferenceSelector;